// Pages that work without signing in: a shared read-only POD, and the invite / password-reset link.
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { PodDocument } from '../components/PodDocument';
import { Loading } from '../components/ui';

export function SharedPod() {
  const { token } = useParams();
  // The first load counts as an opening; background refreshes (every 10 min) do not.
  const [counted] = useState(() => ({ done: false }));
  const q = useQuery({ queryKey: ['share', token], queryFn: () => { const v = counted.done ? '' : '?view=1'; counted.done = true; return api.get<any>(`/api/share/${token}${v}`); }, retry: false, refetchInterval: 10 * 60_000 });
  if (q.isLoading) return <div className="share-page"><Loading /></div>;
  if (q.error) return <div className="share-page"><div className="banner crit">{(q.error as ApiError).message}</div></div>;
  const d = q.data; const doc = d.document;
  return (
    <div className="share-page">
      <div className="share-head no-print">
        <span className="muted small">Read-only POD for {d.label}{d.kind === 'pod_latest' ? ' · always shows the latest published POD' : ''} · link expires {new Date(d.expiresAt).toLocaleDateString()}</span>
        <div className="spacer" style={{ flex: 1 }} />
        {doc && <a className="btn sm" href={`/api/share/${token}/pod.xlsx`}>Download Excel</a>}
        <button className="btn sm" onClick={() => window.print()}>Print / PDF</button>
      </div>
      {!doc ? <div className="banner info">{d.message}</div> : (
        <PodDocument doc={doc} urls={{ logo: (w) => `/api/share/${token}/logo/${w}`, photo: doc.photo ? `/api/share/${token}/photo` : null }} />
      )}
    </div>
  );
}

export function InvitePage() {
  const { token } = useParams();
  const q = useQuery({ queryKey: ['invite', token], queryFn: () => api.get<any>(`/api/invite/${token}`), retry: false });
  const [pw, setPw] = useState(''); const [pw2, setPw2] = useState(''); const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    if (pw.length < 12) return setErr('Use at least 12 characters.');
    if (pw !== pw2) return setErr('The two passwords do not match.');
    setBusy(true);
    try { const r = await api.post<any>(`/api/invite/${token}`, { password: pw }); window.location.href = r.projectId ? `/p/${r.projectId}/pod` : '/'; }
    catch (e2) { setErr((e2 as Error).message); setBusy(false); }
  };
  return (
    <div className="login">
      <div className="login-art"><div><div style={{ fontWeight: 700, letterSpacing: '.04em' }}>CONSTRUCTION INTELLIGENCE PLATFORM</div></div><h1>Set your password to join the project team.</h1><div className="small" style={{ opacity: .7 }}>Your link works once. Your administrator never sees your password.</div></div>
      <div className="login-form">
        {q.isLoading ? <Loading /> : q.error ? <div className="banner crit" style={{ maxWidth: 380 }}>{(q.error as ApiError).message}</div> : (
          <form onSubmit={submit}>
            <h2 style={{ margin: 0 }}>{q.data.purpose === 'reset' ? 'Reset your password' : `Welcome, ${q.data.displayName}`}</h2>
            <div className="small muted">{q.data.email}{q.data.projectName ? ` · ${q.data.projectName}` : ''}</div>
            <label className="field"><span className="req">New password (12+ characters)</span><input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus /></label>
            <label className="field"><span className="req">Repeat password</span><input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></label>
            {err && <div className="banner crit">{err}</div>}
            <button className="btn primary" disabled={busy} style={{ justifyContent: 'center' }}>{busy ? 'Saving…' : 'Set password and sign in'}</button>
          </form>
        )}
      </div>
    </div>
  );
}
